import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useConnectors,
  useCreateSonarQubeConnection,
  useDisableSonarQubeConnection,
  useDisconnectConnector,
  useSaveToken,
  useSonarQubeConnection,
  useTestSonarQubeConnection,
  useTitanEndpointStatus,
  useUpdateSonarQubeConnection,
} from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import {
  ConnectorCatalog,
  ConnectorDetailView,
  DatabaseConnectorsSection,
  SonarQubeConnectorPanel,
  SonarQubeDisableConfirmDialog,
  SonarQubeEnableDialog,
  TokenDialog,
  CONNECTOR_LABELS,
} from '@/components/projects/connectors'
import { ProjectSectionHeader } from '@/components/projects'
import type { ConnectorType, SaveSonarQubeConnectionRequest, TestSonarQubeConnectionRequest } from '@/types'
import { Permissions } from '@/lib/permissions'
import { getErrorCode, getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { describeTokenSaveError } from '@/lib/connectorTokenErrors'

export function ConnectorsPage() {
  const { projectId } = useParams()
  const { hasPermission } = useAuth(projectId)
  const canManage = hasPermission(Permissions.Project.Connectors.Manage, projectId)
  const { data: connectors = [], isLoading: isLoadingConnectors } = useConnectors(projectId!)
  const disconnectConnector = useDisconnectConnector(projectId!)
  const saveToken = useSaveToken(projectId!)
  const { data: titanEndpointStatus } = useTitanEndpointStatus(projectId!)

  const { data: sonarQubeConnection, isLoading: isLoadingSonarQubeConnection } = useSonarQubeConnection(projectId!)
  const createSonarQubeConnection = useCreateSonarQubeConnection(projectId!)
  const updateSonarQubeConnection = useUpdateSonarQubeConnection(projectId!)
  const disableSonarQubeConnection = useDisableSonarQubeConnection(projectId!)
  const testSonarQubeConnection = useTestSonarQubeConnection(projectId!)

  const [selectedType, setSelectedType] = useState<ConnectorType | null>(null)
  const [disconnectingType, setDisconnectingType] = useState<ConnectorType | null>(null)
  const [tokenDialogType, setTokenDialogType] = useState<ConnectorType | null>(null)
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false)
  const [tokenInlineError, setTokenInlineError] = useState<string | null>(null)
  const [sonarQubePanelOpen, setSonarQubePanelOpen] = useState(false)
  const [sonarQubeEnableDialogOpen, setSonarQubeEnableDialogOpen] = useState(false)
  const [sonarQubeDisableConfirmOpen, setSonarQubeDisableConfirmOpen] = useState(false)
  const [sonarQubeTokenInlineError, setSonarQubeTokenInlineError] = useState<string | null>(null)
  const [sonarQubeSaveError, setSonarQubeSaveError] = useState<string | null>(null)

  useEffect(() => {
    setTokenInlineError(null)
  }, [tokenDialogType])

  const handleConnect = (type: ConnectorType) => {
    setTokenDialogType(type)
    setTokenDialogOpen(true)
  }

  const handleCardClick = (type: ConnectorType) => {
    if (type === 'SonarQube') {
      setSonarQubePanelOpen(true)
    } else {
      setSelectedType(type)
    }
  }

  const handleCreateSonarQubeConnection = async (data: SaveSonarQubeConnectionRequest) => {
    setSonarQubeTokenInlineError(null)
    setSonarQubeSaveError(null)
    try {
      await createSonarQubeConnection.mutateAsync(data)
      toast.success('SonarQube connected successfully')
      setSonarQubeEnableDialogOpen(false)
    } catch (error) {
      if (getErrorCode(error) === 'CONNECTOR_UNAUTHORIZED') {
        setSonarQubeTokenInlineError(getErrorMessage(error) ?? 'Invalid token — check your SonarQube token.')
      } else {
        setSonarQubeSaveError(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to create SonarQube connection')
      }
    }
  }

  const handleUpdateSonarQubeConnection = async (data: SaveSonarQubeConnectionRequest) => {
    setSonarQubeTokenInlineError(null)
    setSonarQubeSaveError(null)
    try {
      await updateSonarQubeConnection.mutateAsync(data)
      toast.success('SonarQube connection updated')
    } catch (error) {
      if (getErrorCode(error) === 'CONNECTOR_UNAUTHORIZED') {
        setSonarQubeTokenInlineError(getErrorMessage(error) ?? 'Invalid token — check your SonarQube token.')
      } else {
        setSonarQubeSaveError(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to update SonarQube connection')
      }
    }
  }

  const handleTestSonarQubeConnection = async (data: TestSonarQubeConnectionRequest) => {
    await testSonarQubeConnection.mutateAsync(data)
  }

  const handleDisableSonarQubeConnection = async () => {
    try {
      await disableSonarQubeConnection.mutateAsync()
      toast.success('SonarQube disconnected successfully')
      setSonarQubeDisableConfirmOpen(false)
      setSonarQubePanelOpen(false)
    } catch {
      toast.error('Failed to disable SonarQube')
    }
  }

  const handleSaveToken = async (token: string, email?: string, site?: string) => {
    if (!tokenDialogType) return

    setTokenInlineError(null)
    try {
      await saveToken.mutateAsync({
        connectorType: tokenDialogType,
        data: { token, email, site },
      })
      toast.success(`${CONNECTOR_LABELS[tokenDialogType]} connected successfully`)
      setTokenDialogOpen(false)
      setTokenDialogType(null)
    } catch (error) {
      const message = describeTokenSaveError(error)

      if (message) {
        setTokenInlineError(message)
      } else {
        toast.error(`Failed to connect ${CONNECTOR_LABELS[tokenDialogType]}`)
      }
    }
  }

  const handleDisconnect = async (type: ConnectorType) => {
    setDisconnectingType(type)
    try {
      await disconnectConnector.mutateAsync(type)
      toast.success(`${CONNECTOR_LABELS[type]} disconnected successfully`)
      setSelectedType(null)
    } catch {
      toast.error(`Failed to disconnect ${CONNECTOR_LABELS[type]}`)
    } finally {
      setDisconnectingType(null)
    }
  }

  if (isLoadingConnectors || isLoadingSonarQubeConnection) {
    return <div className="text-muted-foreground" role="status" aria-live="polite" data-testid="connectors-loading">Loading connectors...</div>
  }

  return (
    <div className="space-y-6">
      <ProjectSectionHeader sectionName="Connectors" />

      <ConnectorCatalog
        connectors={connectors}
        onCardClick={handleCardClick}
        canManage={canManage}
        sonarQubeEnabled={!!sonarQubeConnection}
        unavailableReasons={titanEndpointStatus?.configured === false ? { QubizTitan: 'Not set up for your organization' } : undefined}
      />

      <DatabaseConnectorsSection
        projectId={projectId!}
        connectors={connectors}
        canManage={canManage}
      />

      <ConnectorDetailView
        connectorType={selectedType}
        connector={selectedType ? connectors.find((c) => c.type === selectedType) : undefined}
        projectId={projectId!}
        open={selectedType !== null}
        onOpenChange={(open) => { if (!open) setSelectedType(null) }}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        isConnecting={false}
        isDisconnecting={disconnectingType === selectedType}
        canManage={canManage}
      />

      <TokenDialog
        projectId={projectId!}
        connectorType={tokenDialogType}
        open={tokenDialogOpen}
        onOpenChange={(open) => {
          if (!open) setTokenInlineError(null)
          setTokenDialogOpen(open)
        }}
        onSave={handleSaveToken}
        isSaving={saveToken.isPending}
        inlineError={tokenInlineError}
      />

      <SonarQubeConnectorPanel
        connection={sonarQubeConnection ?? undefined}
        open={sonarQubePanelOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSonarQubeTokenInlineError(null)
            setSonarQubeSaveError(null)
          }
          setSonarQubePanelOpen(open)
        }}
        onTest={handleTestSonarQubeConnection}
        onSave={handleUpdateSonarQubeConnection}
        onDisableRequested={() => setSonarQubeDisableConfirmOpen(true)}
        onEnableRequested={() => setSonarQubeEnableDialogOpen(true)}
        isTesting={testSonarQubeConnection.isPending}
        isSaving={updateSonarQubeConnection.isPending}
        canManage={canManage}
        tokenInlineError={sonarQubeTokenInlineError}
        saveError={sonarQubeSaveError}
      />

      <SonarQubeEnableDialog
        open={sonarQubeEnableDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSonarQubeTokenInlineError(null)
            setSonarQubeSaveError(null)
          }
          setSonarQubeEnableDialogOpen(open)
        }}
        onTest={handleTestSonarQubeConnection}
        onSave={handleCreateSonarQubeConnection}
        isTesting={testSonarQubeConnection.isPending}
        isSaving={createSonarQubeConnection.isPending}
        tokenInlineError={sonarQubeTokenInlineError}
        saveError={sonarQubeSaveError}
      />

      <SonarQubeDisableConfirmDialog
        connection={sonarQubeConnection ?? undefined}
        open={sonarQubeDisableConfirmOpen}
        onOpenChange={setSonarQubeDisableConfirmOpen}
        onConfirm={handleDisableSonarQubeConnection}
        isDisabling={disableSonarQubeConnection.isPending}
      />
    </div>
  )
}
